import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Search, Heart, Plane, User, Instagram, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import RegistrationPrompt from "./RegistrationPrompt";

const SIDEBAR_EXPANDED_KEY = 'sidebar_expanded';

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Initialize from localStorage, default to collapsed
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return saved === 'true';
  });

  // Persist expansion state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(isExpanded));
  }, [isExpanded]);

  // Navigation is now available to all users (including unregistered/free tier)
  // Individual features handle their own permission checks via usePermissions hook

  const navItems = [
    { path: "/app/chat", icon: MessageCircle, label: "Chat" },
    { path: "/app/explore", icon: Search, label: "Explore" },
    { path: "/app/saved", icon: Heart, label: "Saved" },
    { path: "/app/plan", icon: MapPin, label: "Plan" },
    { path: "/app/services", icon: "tuk-tuk", label: "Services" },
    { path: "/app/profile", icon: User, label: "Profile" }
  ];

  const handleNavClick = (path: string, e: React.MouseEvent) => {
    // If user is not logged in and trying to access profile, show auth popup
    if (path === "/app/profile" && !authState.user) {
      e.preventDefault();
      setShowAuthModal(true);
      return;
    }
    // Otherwise, navigate normally
    navigate(path);
  };

  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  return (
    <>
      <div 
        className={`hidden md:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out ${
          isExpanded ? 'w-48' : 'w-16'
        }`}
      >
        {/* Toggle Button */}
        <button
          onClick={toggleExpanded}
          className="flex items-center justify-center p-3 hover:bg-gray-100 transition-colors duration-200 border-b border-gray-100"
          title={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <ChevronLeft size={20} className="text-gray-500" />
          ) : (
            <ChevronRight size={20} className="text-gray-500" />
          )}
        </button>

        {/* Nav Items */}
        {navItems.map(({ path, icon, label }) => {
          const isActive = location.pathname === path;
          const isTukTuk = icon === "tuk-tuk";
          return (
            <button
              key={path}
              onClick={(e) => handleNavClick(path, e)}
              className={`flex items-center gap-3 p-4 hover:bg-gray-100 transition-colors duration-200 ${
                isActive ? 'text-gray-900 bg-gray-100' : 'text-gray-500 hover:text-gray-700'
              } ${isExpanded ? 'justify-start' : 'justify-center'}`}
              title={!isExpanded ? label : undefined}
            >
              {isTukTuk ? (
                <img 
                  src="/tuk-tuk.png" 
                  alt="Tuk Tuk" 
                  className={`w-6 h-6 flex-shrink-0 ${isActive ? 'opacity-100' : 'opacity-60'}`}
                />
              ) : (
                React.createElement(icon, { size: 24, className: `flex-shrink-0 ${isActive ? 'fill-current' : ''}` })
              )}
              {isExpanded && (
                <span className={`text-sm font-medium whitespace-nowrap overflow-hidden ${
                  isActive ? 'text-gray-900' : 'text-gray-600'
                }`}>
                  {label}
                </span>
              )}
            </button>
          );
        })}
        
        {/* Instagram / Featured */}
        <button
          onClick={(e) => handleNavClick("/app/featured", e)}
          className={`flex items-center gap-3 p-4 hover:bg-gray-100 transition-colors duration-200 ${
            location.pathname === "/app/featured" ? 'bg-gray-100' : ''
          } ${isExpanded ? 'justify-start' : 'justify-center'}`}
          title={!isExpanded ? "Featured" : undefined}
        >
          <Instagram 
            size={28} 
            strokeWidth={1.5}
            className={`flex-shrink-0 ${location.pathname === "/app/featured" ? 'fill-current' : ''}`}
            style={{ color: '#663399' }}
          />
          {isExpanded && (
            <span className={`text-sm font-medium whitespace-nowrap overflow-hidden ${
              location.pathname === "/app/featured" ? 'text-gray-900' : 'text-gray-600'
            }`}>
              Featured
            </span>
          )}
        </button>
      </div>

      <RegistrationPrompt
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        variant="google_auth"
        trigger="profile"
      />
    </>
  );
}
